import React, { useContext, useState } from 'react';
import { useTheme } from '@emotion/react';
import { AgendaContext } from '../../../contexts/AgendaContext.jsx';
// Mui components
import { Typography, Button, Tooltip, Box } from '@mui/material';
// Components
import HorizontalDivider from '../../common/HorizontalDivider.jsx';
import ChipEstadoSolicitud from '../SolicitudCard/ChipEstadoSolicitud.jsx';
import EspecialidadesList from './EspecialidadesList.jsx';
import ModalMotivoRechazo from '../Modals/ModalMotivoRechazo.jsx';
// DayJS
import dayjs from 'dayjs';
// Hooks
import { toCamelCaseAllWords } from '../../../utils/Hooks/textFormatted.js';
// Icons
import iconCircleInformation from '../../../assets/icons/icon_circle-information.svg';

function SeccionInfoSolicitud({ handleOpen }) {
  const theme = useTheme();

  const { solicitudDetail, motivosRechazo } = useContext(AgendaContext);
  const [modalMotivoRechazoIsVisible, setModalMotivoRechazoIsVisible] = useState(false);

  const handleOpenModalMotivoRechazo = () => {
    setModalMotivoRechazoIsVisible(!modalMotivoRechazoIsVisible);
  };

  const getDescripcionMotivo = () => {
    return motivosRechazo?.find((mr) => mr?.codMotivoRechazo === solicitudDetail?.idMotivoRechazo)
      ?.descripcion;
  };

  const rechazoConMotivo =
    solicitudDetail?.idMotivoRechazo != null &&
    solicitudDetail?.idMotivoRechazo != undefined &&
    solicitudDetail?.idMotivoRechazo != 6;

  const direccionOsdeArray = solicitudDetail?.direccionOsde?.split(',');
  const direccionOsdeFormateada = [...new Set(direccionOsdeArray)].join(', ');

  const direccionExternoArray = solicitudDetail?.direccionExterno?.split(',');
  const direccionExternoFormateada = [...new Set(direccionExternoArray)].join(', ');

  const telefonos = solicitudDetail?.datosContacto?.filter(
    (contacto) => contacto.system === 'phone'
  );
  const emails = solicitudDetail?.datosContacto?.filter((contacto) => contacto.system === 'email');

  const especialidadesSolicitadas = solicitudDetail?.especializacion?.filter(
    (especialidad) => especialidad.solicitada
  );

  const especialidadesNoSolicitadas = solicitudDetail?.especializacion?.filter(
    (especialidad) => !especialidad.solicitada
  );

  const especialidadesAprobadas = solicitudDetail?.especializacion?.filter(
    (especialidad) => especialidad.aprobada
  );

  const especialidadesNoAprobadas = solicitudDetail?.especializacion?.filter(
    (especialidad) => !especialidad.aprobada
  );

  const solicitudParcialmenteAprobada =
    solicitudDetail?.tipoSolicitud === 'MODIFICAR_CONSULTORIO' &&
    solicitudDetail?.status === 'APROBADO' &&
    solicitudDetail?.especializacion.some(
      (especialidad) => especialidad.solicitada && !especialidad.aprobada
    );

  const solicitudAlta = solicitudDetail?.tipoSolicitud !== 'MODIFICAR_CONSULTORIO';

  const solicitudModificacion = solicitudDetail?.tipoSolicitud === 'MODIFICAR_CONSULTORIO';

  return (
    solicitudDetail && (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: 'auto',
          border: `2px solid ${theme.palette.backgroundSecondary.main}`,
          borderRadius: '1rem',
          gap: '1.5rem',
          padding: '1.5rem',
          flexDirection: 'column'
        }}>
        <ModalMotivoRechazo
          modalIsVisible={modalMotivoRechazoIsVisible}
          motivoRechazo={motivosRechazo?.find(
            (mr) => mr?.codMotivoRechazo === solicitudDetail?.idMotivoRechazo
          )}
          handleClose={handleOpenModalMotivoRechazo}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h4">
            {solicitudDetail?.tipoSolicitud === 'ALTA_CONSULTORIO'
              ? 'Solicitud de conexión de agendas'
              : solicitudDetail?.tipoSolicitud === 'BAJA_CONSULTORIO'
              ? 'Solicitud de desconexión de agendas'
              : 'Solicitud de modificación'}
          </Typography>
          {solicitudDetail?.status === 'APROBADO' ? (
            <ChipEstadoSolicitud
              backGroundColor={theme.palette.successBackground.main}
              text="Aprobada"
              colorText={theme.palette.successSecondary.main}
            />
          ) : (solicitudDetail?.status === 'PENDIENTE' || solicitudDetail?.status === 'PLANIFICADO') ? (
            <ChipEstadoSolicitud
              backGroundColor={theme.palette.backgroundSecondary.main}
              text="En análisis"
              colorText={theme.palette.primary.main}
            />
          ) : solicitudDetail?.status === 'RECHAZADO' ? (
            <ChipEstadoSolicitud
              backGroundColor={theme.palette.errorBackground.main}
              text="Rechazada"
              colorText={theme.palette.errorSecondary.main}
            />
          ) : solicitudDetail?.status === 'ANULADO' ? (
            <ChipEstadoSolicitud
              backGroundColor={theme.palette.graySuperLight.main}
              text="Anulada"
              colorText={theme.palette.black.main}
            />
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '45%' }}>
            <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
              Dirección en {toCamelCaseAllWords(solicitudDetail?.proveedor)}
            </Typography>
            <Tooltip title={direccionExternoFormateada} placement="bottom-start">
              <Typography
                variant="h5"
                component="p"
                sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {direccionExternoFormateada}
              </Typography>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', width: '45%' }}>
            <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
              Dirección en OSDE
            </Typography>
            <Tooltip title={direccionOsdeFormateada} placement="bottom-start">
              <Typography
                variant="h5"
                component="p"
                sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {direccionOsdeFormateada}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '45%' }}>
            <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
              Filial
            </Typography>
            <Typography variant="h5">{toCamelCaseAllWords(solicitudDetail?.filial)}</Typography>
          </Box>
          {solicitudDetail?.status === 'RECHAZADO' && rechazoConMotivo && (
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '45%' }}>
              <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
                Motivo de rechazo
              </Typography>
              <Typography variant="h5" sx={{ display: 'flex' }}>
                {getDescripcionMotivo()}
                <Button
                  variant="text"
                  disableRipple
                  onClick={() => handleOpenModalMotivoRechazo()}
                  sx={{
                    color: theme.palette.primary.main,
                    display: 'flex',
                    width: 'auto',
                    alignItems: 'flex-start',
                    padding: '0px 0px 0px 1rem',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}>
                  <Typography
                    variant="textLink"
                    sx={{ textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                    Ver más
                  </Typography>
                </Button>
              </Typography>
            </Box>
          )}
        </Box>

        {solicitudDetail?.especializacion?.length > 0 && (
          <>
            <HorizontalDivider />

            <Typography variant="h4">
              {solicitudDetail?.tipoSolicitud === 'ALTA_CONSULTORIO' &&
              solicitudDetail?.status !== 'APROBADO'
                ? 'Especialidades seleccionadas'
                : solicitudDetail?.tipoSolicitud === 'MODIFICAR_CONSULTORIO'
                ? 'Especialidades'
                : 'Especialidades publicadas'}
            </Typography>

            {solicitudAlta && (
              <EspecialidadesList
                especialidades={
                  solicitudDetail?.tipoSolicitud === 'ALTA_CONSULTORIO' &&
                  solicitudDetail?.status !== 'APROBADO'
                    ? especialidadesSolicitadas
                    : especialidadesAprobadas
                }
              />
            )}
          </>
        )}

        {solicitudModificacion && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '45%' }}>
              <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
                {solicitudDetail?.status !== 'APROBADO'
                  ? 'Conexión de agendas solicitadas para:'
                  : 'Especialidades conectadas'}
              </Typography>
              <EspecialidadesList
                especialidades={
                  solicitudDetail?.status !== 'APROBADO'
                    ? especialidadesSolicitadas
                    : especialidadesAprobadas
                }
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', width: '45%' }}>
              <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
                {solicitudDetail?.status !== 'APROBADO'
                  ? 'Desconexión de agendas solicitadas para:'
                  : 'Especialidades no conectadas'}
              </Typography>
              <EspecialidadesList
                especialidades={
                  solicitudDetail?.status !== 'APROBADO'
                    ? especialidadesNoSolicitadas
                    : especialidadesNoAprobadas
                }
              />
            </Box>
          </Box>
        )}

        {solicitudParcialmenteAprobada && (
          <Box
            sx={{
              backgroundColor: theme.palette.backgroundSecondary.main,
              borderRadius: '1rem',
              width: '100%',
              minHeight: '48px',
              padding: '8px 24px',
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
              alignItems: 'center'
            }}>
            <Box
              component="img"
              sx={{ width: '16px', height: '16px' }}
              alt="img not found"
              src={iconCircleInformation}
            />
            <Typography variant="bodyS" component="p">
              Algunas especialidades no fueron conectadas.
            </Typography>
          </Box>
        )}

        <HorizontalDivider />
        {solicitudDetail?.tipoSolicitud === 'ALTA_CONSULTORIO' &&
          solicitudDetail.status === 'PENDIENTE' && (
            <>
              <Typography variant="h4">Fecha de publicación solicitada</Typography>
              <Typography variant="h5">
                {' '}
                {dayjs(solicitudDetail?.fechaPublicacion).format('DD/MM/YYYY')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                <Box
                  component="img"
                  sx={{ width: '16px', height: '16px' }}
                  alt="img not found"
                  src={iconCircleInformation}
                />
                <Typography variant="bodyS">
                  Esta fecha es de referencia. Una vez aprobada la solicitud te confirmaremos la
                  fecha de publicación definitiva.
                </Typography>
              </Box>

              <HorizontalDivider />
            </>
          )}
        <Typography variant="h4">Datos de contacto</Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
            Teléfonos
          </Typography>
          <Box sx={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', wordBreak: 'break-word' }}>
            {telefonos.length > 0 ? (
              telefonos.map((telefono, index) => (
                <Box key={index} sx={{ display: 'flex', gap: '0.5rem' }}>
                  <Typography component="span" variant="h5">
                    {telefono.value}
                  </Typography>
                  <Typography component="span" variant="h5">
                    {index < telefonos.length - 1 && ' | '}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography component="span" variant="h5">
                -
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="bodyS" sx={{ color: theme.palette.gray.main }}>
            Correos electrónicos
          </Typography>
          <Box sx={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', wordBreak: 'break-word' }}>
            {emails.length > 0 ? (
              emails.map((email, index) => (
                <Box key={index} sx={{ display: 'flex', gap: '0.5rem' }}>
                  <Typography component="span" variant="h5">
                    {email.value}
                  </Typography>
                  <Typography component="span" variant="h5">
                    {index < emails.length - 1 && ' | '}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography component="span" variant="h5">
                -
              </Typography>
            )}
          </Box>
        </Box>

        {solicitudDetail?.status === 'PENDIENTE' && (
          <Button
            variant="text"
            disableRipple
            onClick={() => handleOpen()}
            sx={{
              color: theme.palette.primary.main,
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '0px 1rem 0px 0px',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}>
            <Typography variant="textLink" sx={{ textDecoration: 'underline' }}>
              Anular solicitud
            </Typography>
          </Button>
        )}
      </Box>
    )
  );
}

export default SeccionInfoSolicitud;
